import crownGold from "assets/images/crownGold.svg"
import { useGraphqlQuery } from "graphql-mock"
import { IconCertified } from "materia"
import { useTranslation } from "react-i18next"
import { displayTimestamp, exists } from "utils"
import { useGetOrdinal } from "../../../dataUtilities/getOrdinal"
import { Avatar } from "../../Avatar"
import { Stars } from "../../Stars"
import { Widget } from "../../Widget"
import { query, type RecentActivityEntry } from "./gql"
import s from "./RecentActivityWidget.module.scss"

export const RecentActivityWidgetConnected = () => {
  const { data, error, isPending } = useGraphqlQuery(query, {})

  const { completed, leaderboard, stars } = data ?? {}

  const activityEntries = [completed, leaderboard, stars]
    .map((activityEntry) => activityEntry?.nodes[0])
    .filter(exists)

  return (
    <RecentActivityWidget
      isLoading={isPending}
      error={error}
      activityEntries={activityEntries}
    />
  )
}

type RecentActivityWidgetProps = {
  isLoading: boolean
  error: unknown
  activityEntries: RecentActivityEntry[]
}
export const RecentActivityWidget = ({
  isLoading,
  error,
  activityEntries,
}: RecentActivityWidgetProps) => {
  const { t } = useTranslation()

  const getContent = () => {
    if (isLoading)
      return (
        <>
          <ActivityEntry.Skeleton />
          <ActivityEntry.Skeleton />
          <ActivityEntry.Skeleton />
        </>
      )

    if (error) return <>{t("error.RECENT_ACTIVITY_FETCH_ERROR")}</>

    return (
      <>
        {activityEntries.map((entry, index) => (
          <ActivityEntry
            key={index}
            avatar={entry.user.avatar}
            avatarTitle={entry.user.avatarTitle}
            createdAt={entry.createdAt}
            displayName={entry.user.displayName}
            eventType={entry.eventType}
            productTitle={entry.product.title}
            rank={entry.rank}
            stars={entry.stars}
          />
        ))}
      </>
    )
  }

  // TODO [widgets-flash] https://jira.attensi.com/browse/WEB-19450
  // Customers requested that this widget disappears when it has no content.
  const isEmpty = activityEntries.length === 0 && !isLoading && !error
  if (isEmpty) return null

  return (
    <Widget title={t("activityFeed.activity")} error={error}>
      <ul className={s.activityEntries}>{getContent()}</ul>
    </Widget>
  )
}

// TODO [api-decoupling]
// Interesting thing: there is this component called RecentActivityWidgetModal
// which lives in its own route. It can be opened by clicking the mobile widget
// with recent activity. Its design is pretty much the same as this widget.
// Those two could be reconcilled, but not in this PR.
type ActivityEntryProps = {
  avatar: string
  avatarTitle: string
  displayName: string | null
  productTitle: string | null
  createdAt: string
  eventType: string
  rank: number | null
  stars: number | null
}
const ActivityEntry = ({
  avatar,
  avatarTitle,
  displayName,
  productTitle,
  createdAt,
  eventType,
  rank,
  stars,
}: ActivityEntryProps) => {
  const { t, i18n } = useTranslation()
  const getOrdinal = useGetOrdinal()

  const getSummaryText = () => {
    if (eventType === "stars" && stars)
      return t("activityFeed.stars", { count: stars })
    if (eventType === "leaderboard" && rank) return getOrdinal(rank)
    return t("activityFeed.completed")
  }

  const getIcon = () => {
    if (eventType === "completed") {
      return <IconCertified />
    }
    if (eventType === "stars") {
      return <Stars count={1} max={1} size="extraSmall" />
    }
    if (eventType === "leaderboard") {
      return <img className={s.entry__icon} src={crownGold} alt="" />
    }
  }

  return (
    <li className={s.entry}>
      <span className={s.entry__avatarWrapper}>
        <Avatar
          className={s.entry__avatarWrapper}
          alt={avatarTitle}
          image={avatar}
          size="medium"
        />
      </span>

      <div className={s.entry__section}>
        <div className={s.entry__name}>{displayName}</div>
        <div className={s.entry__time}>
          {displayTimestamp(createdAt, t, i18n.language)}
        </div>
      </div>

      <div className={s.entry__section}>
        {getIcon()}
        <div className={s.entry__summary}>{getSummaryText()}</div>
        <div className={s.entry__product}>{productTitle}</div>
      </div>
    </li>
  )
}

ActivityEntry.Skeleton = () => (
  <li className={s.entry}>
    <span className={s.entry__avatarWrapper}>
      <Avatar.Skeleton size="medium" />
    </span>
    <div className={s.skeleton__title} />
    <div className={s.skeleton__summary} />
  </li>
)
